import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Can } from "@/casl.config";

import { useGetCustomerQuery } from "@redux/features/customers/customersApi";

import GeneralTabPreview from "../components/tabs/previews/GeneralTabPreview";
import DiscountTab from "../components/tabs/previews/DiscountTab";
import AddressTabPreview from "../components/tabs/previews/AddressTabPreview";
import SettingsTab from "../components/tabs/previews/SettingsTab";
import SaleOrdersTab from "../components/tabs/previews/SaleOrdersTab";

import DeletionModal from "../components/modals/DeletionModal";

import BaseRecordRenderer from "@reusables/BaseRecordRenderer";
import { BaseTinyActionIcon, TinyActionIconType } from "@reusables/BaseTinyActionIcon";
import BaseTooltip from "@reusables/BaseTooltip";
import BaseTabs from "@reusables/BaseTabs";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import IntegrationsBadge from "../components/IntegrationsBadge";

export default function CustomerDetailsPage() {
    const { t } = useTranslation("", { keyPrefix: "customers.general.tabs" });
    const history = useHistory();

    const { id } = useParams<{ id: string, page: string }>();
    const { data: customer, isLoading: customerLoading } = useGetCustomerQuery(parseInt(id));
    const [isDeletionModalOpen, setIsDeletionModalOpen] = useState<boolean>(false);

    return (
        <>
            <Can not I="view" a="customer">
                <div className="levitation-extended">
                    <NoPermissionBanner />
                </div>
            </Can>

            <Can I="view" a="customer">
                <BaseRecordRenderer
                    record={customer}
                    loading={customerLoading}
                >
                    <div className="flex items-center mb-[40px]">
                        <div className={"flex flex-row items-center grow space-x-3"}>
                            <div className="text-2xl font-bold text-accent">
                                {
                                    customer?.name
                                }
                            </div>
                            <IntegrationsBadge row={customer} width={24} height={24} />
                        </div>
                        <div className="space-x-[8px] flex items-center h-full">
                            <BaseTooltip title={`${t("sideButtons.createSalesOrder")}`} placement="bottom-start">
                                <Can I="create" a="sale_order">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.SalesOrder}
                                        onClick={() => history.push("/dashboard/sales/orders/new", { customer: customer })}
                                    />
                                </Can>
                            </BaseTooltip>

                            <BaseTooltip title={`${t("sideButtons.edit")}`} placement="bottom">
                                <Can I="edit" a="customer">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.Edit}
                                        onClick={() => history.push(`/dashboard/customers/${id}/edit`)}
                                    />
                                </Can>
                            </BaseTooltip>

                            <BaseTooltip title={`${t("sideButtons.delete")}`} placement="bottom-end">
                                <Can I="delete" a="customer">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.Delete}
                                        onClick={() => setIsDeletionModalOpen(true)}
                                    />
                                </Can>
                            </BaseTooltip>
                        </div>
                    </div>

                    <>
                        <BaseTabs tabs={[
                            {
                                key: "general",
                                title: t("general.heading"),
                                content: <GeneralTabPreview customer={customer!} />
                            },
                            {
                                key: "discount",
                                title: t("pricesAndDiscounts.heading"),
                                content: <DiscountTab customer={customer!} />
                            },
                            {
                                key: "address",
                                title: t("address.heading"),
                                content: <AddressTabPreview customer={customer!} />
                            },
                            {
                                key: "settings",
                                title: t("settings.heading"),
                                content: <SettingsTab customer={customer!} />
                            },
                            {
                                key: "salesOrders",
                                title: t("salesOrders.heading"),
                                content: <SaleOrdersTab customer={customer!} />
                            }
                        ]} />
                    </>
                </BaseRecordRenderer>
            </Can>

            <DeletionModal
                isOpen={isDeletionModalOpen}
                onClose={() => setIsDeletionModalOpen(false)}
                onDeletion={() => history.push("/dashboard/customers")}
                entity={customer}
            />
        </>
    );
}